import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, Button, Field, Alert, Spinner } from "@/components/ui";
import { Logo } from "@/components/Logo";
import { ShieldCheck, PartyPopper, Users } from "lucide-react";

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-surface flex flex-col items-center px-6 py-10">
      <div className="w-full max-w-md">
        <div className="mb-8"><Logo className="h-9" /></div>
        {children}
      </div>
    </div>
  );
}

const ROLE_COPY = {
  family_member: "You'll be able to log medications, vitals and events alongside them.",
  aide: "You'll see shifts and sign-offs for the care you provide.",
  viewer: "You'll be able to read the circle — you won't change anything.",
  primary_caregiver: "You'll share full responsibility for this circle.",
};

/**
 * Joining a care circle from an emailed invitation.
 *
 * Two routes in, because the invited person may or may not already have an
 * account, and the backend refuses to guess: the token in the link is the only
 * proof they own the address, since signup never verifies email.
 */
export default function FamilyInvite() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user, refresh } = useAuth();
  const [state, setState] = useState("loading"); // loading | ready | error | done
  const [preview, setPreview] = useState(null);
  const [errMsg, setErrMsg] = useState(null);
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [formErr, setFormErr] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        setPreview(await api.peekFamilyInvite(token));
        setState("ready");
      } catch (e) {
        setErrMsg(e?.detail || e?.message || "This invitation is no longer valid.");
        setState("error");
      }
    })();
  }, [token]);

  const acceptSignedIn = async () => {
    setFormErr(null); setBusy(true);
    try {
      await api.acceptFamilyInviteAsUser(token);
      await refresh();
      setState("done");
    } catch (e) {
      setFormErr(e?.detail || e?.message || "Couldn't join the circle.");
    } finally { setBusy(false); }
  };

  const acceptNew = async () => {
    setFormErr(null);
    if (password.length < 8) return setFormErr("Use a password of at least 8 characters.");
    if (password !== confirm) return setFormErr("Passwords don't match.");
    setBusy(true);
    try {
      await api.acceptFamilyInvite({ token, password, display_name: name.trim() || undefined });
      await refresh();
      setState("done");
    } catch (e) {
      setFormErr(e?.detail || e?.message || "Couldn't create your account. Ask them to resend the invitation.");
    } finally { setBusy(false); }
  };

  if (state === "loading") return <Shell><Spinner /></Shell>;

  if (state === "error") return (
    <Shell>
      <Card>
        <h1 className="font-display text-2xl text-ink">Invitation not available</h1>
        <p className="text-sm text-subtle mt-2" data-testid="finv-error-detail">{errMsg}</p>
        <p className="text-sm text-subtle mt-2">Invitations last 14 days and can only be used once. Ask whoever invited you to send a new one.</p>
        <Link to="/login"><Button variant="secondary" className="mt-4" data-testid="finv-goto-login">Go to sign in</Button></Link>
      </Card>
    </Shell>
  );

  if (state === "done") return (
    <Shell>
      <Card className="text-center" data-testid="finv-success">
        <div className="mx-auto w-14 h-14 rounded-full bg-mint-tint flex items-center justify-center mb-4"><PartyPopper className="w-7 h-7 text-mint-dark" /></div>
        <h1 className="font-display text-2xl text-ink">You&apos;re in the circle</h1>
        <p className="text-sm text-subtle mt-2">Everything the family has been keeping track of is now shared with you.</p>
        <Button className="w-full mt-5" data-testid="finv-continue" onClick={() => navigate("/app", { replace: true })}>Continue to KinBridge</Button>
      </Card>
    </Shell>
  );

  const circle = preview.family_name ? `${preview.family_name}'s care circle` : "a care circle";
  const signedInAlready = Boolean(user);

  return (
    <Shell>
      <h1 className="font-display text-3xl text-ink" data-testid="finv-title">You&apos;ve been invited to help</h1>
      <p className="text-sm text-subtle mt-2 mb-6" data-testid="finv-blurb">
        {preview.invited_by || "A family member"} invited you to join {circle} as a {String(preview.role || "family_member").replace(/_/g, " ")}.
        {preview.circle_size > 1 ? ` ${preview.circle_size} people are already helping.` : ""}
      </p>

      <Card>
        <div className="flex items-start gap-3 mb-4">
          <div className="w-9 h-9 rounded-full bg-brand-tint flex items-center justify-center shrink-0"><Users className="w-4 h-4 text-brand" /></div>
          <p className="text-sm text-subtle">{ROLE_COPY[preview.role] || ROLE_COPY.family_member}</p>
        </div>

        {signedInAlready ? (
          <>
            <p className="text-sm text-ink mb-4">You&apos;re signed in as <strong>{user.email}</strong>. The invitation has to match that address.</p>
            <Alert testid="finv-error">{formErr}</Alert>
            <Button className="w-full" data-testid="finv-accept-signed-in" loading={busy} onClick={acceptSignedIn}>Join the circle</Button>
          </>
        ) : preview.has_account ? (
          <>
            <p className="text-sm text-ink mb-4">This email already has a KinBridge account. Sign in, then open this link again to join.</p>
            <Link to={`/login?next=/family-invite/${token}`}>
              <Button className="w-full" data-testid="finv-signin-first">Sign in to join</Button>
            </Link>
          </>
        ) : (
          <>
            <div className="text-xs font-semibold text-brand uppercase tracking-wide mb-3">Create your account</div>
            <Field label="Your name" data-testid="finv-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="How the family will see you" />
            <Field label="Password" type="password" data-testid="finv-password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="At least 8 characters" />
            <Field label="Confirm password" type="password" data-testid="finv-confirm" value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Retype your password" />
            <Alert testid="finv-error">{formErr}</Alert>
            <Button className="w-full mt-1" data-testid="finv-accept" loading={busy} onClick={acceptNew} disabled={!password || !confirm}>Join the circle</Button>
          </>
        )}

        <p className="flex items-center justify-center gap-1.5 text-xs text-subtle mt-4"><ShieldCheck className="w-3.5 h-3.5" /> Protected under HIPAA-aligned safeguards. Leave the circle anytime.</p>
      </Card>
    </Shell>
  );
}
